"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TimeAndDate = void 0;
const tslib_1 = require("tslib");
const errors_1 = require("@zap-onboard/errors");
const dayjs_1 = (0, tslib_1.__importDefault)(require("dayjs"));
const duration_1 = (0, tslib_1.__importDefault)(require("dayjs/plugin/duration"));
const relativeTime_1 = (0, tslib_1.__importDefault)(require("dayjs/plugin/relativeTime"));
const utc_1 = (0, tslib_1.__importDefault)(require("dayjs/plugin/utc"));
const timezone_1 = (0, tslib_1.__importDefault)(require("dayjs/plugin/timezone"));
const advancedFormat_1 = (0, tslib_1.__importDefault)(require("dayjs/plugin/advancedFormat"));
const AbsoluteDate_1 = require("./AbsoluteDate");
dayjs_1.default.extend(timezone_1.default);
dayjs_1.default.extend(utc_1.default);
dayjs_1.default.extend(advancedFormat_1.default);
dayjs_1.default.extend(duration_1.default);
dayjs_1.default.extend(relativeTime_1.default);
/**
 * Date class that is serialized to/from JSON using ISO timestamps
 */
class TimeAndDate extends Date {
    /*
     * Parses dates in the C#/Dotnet format
     * NOTE: This is ripped directly from the xero-node API client
     * '/Date(1616071638000+1000)/'
     * '/Date(1616071638000-3000)/'
     * '/Date(1616071638000)/'
     */
    static fromDotNetString(dns) {
        const isDate = new Date(dns);
        if (Number.isNaN(isDate.getTime())) {
            const m = /-?\d+/.exec(dns);
            if (!m) {
                throw errors_1.errs.UnexpectedError.create('Xero returned an unexpected response', {
                    details: {
                        m,
                        dns,
                    },
                });
            }
            return TimeAndDate.fromDate(new Date(parseInt(m[0], 10)));
        }
        return TimeAndDate.fromDate(isDate);
    }
    static fromDateStringFormat(dateString, format) {
        if (typeof dateString !== 'string') {
            throw new TypeError(`Invalid date string: ${dateString}`);
        }
        if (!(0, dayjs_1.default)(dateString).isValid()) {
            throw new TypeError(`Invalid date string: ${dateString}`);
        }
        const date = (0, dayjs_1.default)(dateString, format);
        return TimeAndDate.fromDate(date.toDate());
    }
    static get dotNetString() {
        return '/Date(1616071638000)/';
    }
    static fromMS(s) {
        return TimeAndDate.fromDate(new Date(s));
    }
    static fromSeconds(s) {
        return TimeAndDate.fromDate(new Date(s * 1000));
    }
    static fromDate(date) {
        return new TimeAndDate(date.toISOString());
    }
    static present() {
        return this.fromDate(new Date());
    }
    static fromJSON(json) {
        if (json instanceof Date) {
            return TimeAndDate.fromDate(json);
        }
        if (typeof json !== 'string') {
            throw new TypeError(`cannot deserialize time from non string ${json}`);
        }
        return new TimeAndDate(json);
    }
    constructor(iso) {
        super(iso);
    }
    add(units) {
        return TimeAndDate.fromDate((0, dayjs_1.default)(this).add(dayjs_1.default.duration(units)).toDate());
    }
    subtract(units) {
        return TimeAndDate.fromDate((0, dayjs_1.default)(this).subtract(dayjs_1.default.duration(units)).toDate());
    }
    isInPast() {
        return this.hasntPassed();
    }
    isInFuture() {
        return this.hasPassed();
    }
    isAfter(date = new Date()) {
        return this.hasPassed(date);
    }
    isBefore(date = new Date()) {
        return !this.isAfter(date);
    }
    hasPassed(date = new Date()) {
        return (0, dayjs_1.default)(this).isAfter(date);
    }
    hasntPassed(date = new Date()) {
        return !this.hasPassed(date);
    }
    toDotNetString() {
        return `/Date(${this.getTime() / 1000})/`;
    }
    toString() {
        return `TimeAndDate{${this.toISOString()}}`;
    }
    /*
     * Format into an exact timestamp for display to users
     * Thursday, August 16, 2018 8:02 PM
     */
    toTimestampString() {
        return (0, dayjs_1.default)(this).format('dddd, MMMM D, YYYY h:mm A');
    }
    /*
     * Format into an exact timestamp for display to users
     * 1-08-18 8:02 pm
     */
    toShortTimestampString() {
        return (0, dayjs_1.default)(this).format('D[-]MM[-]YY h:mm a');
    }
    /**
     * Returns in the format 2009-11-12T00:00:00.
     *
     * This is the format used by E-Tag and If-Modified-Since HTTP headers.
     */
    toUTCTimestampString() {
        return (0, dayjs_1.default)(this).utc().format('YYYY-MM-DDTHH:mm:ss');
    }
    toFormat(format) {
        return (0, dayjs_1.default)(this).format(format);
    }
    toDMY() {
        return (0, dayjs_1.default)(this).format('DD/MM/YY');
    }
    toTimeFromNow() {
        return (0, dayjs_1.default)(this).fromNow();
    }
    toAbsoluteDate() {
        return AbsoluteDate_1.AbsoluteDate.fromDate(this);
    }
    asJSON() {
        return this.toISOString();
    }
    inSeconds() {
        return this.getTime() / 1000;
    }
    inMS() {
        return this.getTime();
    }
    async wait() {
        const waitForMs = Math.max(1, this.inMS() - TimeAndDate.present().inMS());
        // eslint-disable-next-line no-promise-executor-return
        await new Promise((res) => setTimeout(res, waitForMs));
    }
}
exports.TimeAndDate = TimeAndDate;
